import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Award, Star, Trophy, TrendingUp, Target, ArrowUp, Shield, Zap, Activity, Package, ChevronRight, ChevronDown, AlertCircle, X } from 'lucide-react';

const PlayerBadges = ({ badges = [] }) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [visibleInfoBadge, setVisibleInfoBadge] = useState(null);
  
  // Style for better-looking badge descriptions
  const formatDescription = (description) => {
    if (!description) return '';
    
    // Split into sentences for better formatting
    const sentences = description.split(/(?<=[.!?])\s+/);
    
    return (
      <div className="space-y-2">
        {sentences.map((sentence, index) => (
          <p key={index}>{sentence}</p>
        ))}
      </div>
    );
  };
  
  // Update the data processing to ensure all gameweeks are captured
  const badgesByCategory = React.useMemo(() => {
    // First, let's ensure we're using the right data source
    console.log('Processing badges data:', badges);
    
    const categories = {
      'Performance': {},
      'Captain': {},
      'Differential': {},
      'Consistency': {},
      'Milestones': {},
      'Special': {},
      'Other': {}
    };
    
    // Define which badge goes into which category
    const categoryMappings = {
      'Diamond in the Rough': 'Differential',
      'On the Money': 'Captain',
      'Full House': 'Performance',
      'The Comeback': 'Consistency',
      'Mastermind': 'Captain',
      'Overachiever': 'Performance',
      'Gameweek Champion': 'Performance',
      'Centurion': 'Milestones',
      'Trailblazer': 'Consistency',
      'Final Flourish': 'Consistency',
      'Hot Streak': 'Consistency',
      'Bench Blunder': 'Special',
      'Tactical Genius': 'Special',
      'Legendary Performance': 'Milestones',
      'Personal Best': 'Milestones',
      'FPL Manager': 'Other'
    };
    
    // Group badges by name and collect gameweeks for each badge type
    badges.forEach(badge => {
      const category = categoryMappings[badge.name] || 'Other';
      
      // If this badge type doesn't exist in the category yet, initialize it
      if (!categories[category][badge.name]) {
        categories[category][badge.name] = {
          name: badge.name,
          gameweeks: [],
          instances: []
        };
      }
      
      // Check if this gameweek is already in the list (avoid duplicates)
      if (!categories[category][badge.name].gameweeks.includes(badge.gameweek)) {
        categories[category][badge.name].gameweeks.push(badge.gameweek);
      }
      
      // Sort gameweeks numerically
      categories[category][badge.name].gameweeks.sort((a, b) => a - b);
      
      // Add the full badge instance for reference
      categories[category][badge.name].instances.push(badge);
    });
    
    // For debugging: check all gameweeks collected
    Object.entries(categories).forEach(([category, badgeMap]) => {
      Object.entries(badgeMap).forEach(([badgeName, badgeData]) => {
        console.log(`${category} - ${badgeName}: Gameweeks`, badgeData.gameweeks);
      });
    });
    
    // Convert the badge objects to arrays for each category
    const categoriesWithArrays = {};
    Object.entries(categories).forEach(([category, badgeMap]) => {
      // Only include categories with badges
      if (Object.keys(badgeMap).length > 0) {
        categoriesWithArrays[category] = Object.values(badgeMap);
      }
    });
    
    return categoriesWithArrays;
  }, [badges]);
  
  // Initialize expanded state for categories with badges
  useEffect(() => {
    const initialExpandedState = {};
    Object.keys(badgesByCategory).forEach(category => {
      initialExpandedState[category] = true; // Start with all categories expanded
    });
    setExpandedCategories(initialExpandedState);
  }, [badgesByCategory]);
  
  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Render badge icon based on badge type
  const renderBadgeIcon = (badgeName, size = 16) => {
    switch (badgeName) {
      case 'Diamond in the Rough': return <Star size={size} className="text-cyan-200" />;
      case 'On the Money': return <Trophy size={size} className="text-amber-200" />;
      case 'Full House': return <Trophy size={size} className="text-purple-200" />;
      case 'The Comeback': return <TrendingUp size={size} className="text-emerald-200" />;
      case 'Mastermind': return <Target size={size} className="text-indigo-200" />;
      case 'Overachiever': return <ArrowUp size={size} className="text-blue-200" />;
      case 'Gameweek Champion': return <Trophy size={size} className="text-red-200" />;
      case 'Centurion': return <Shield size={size} className="text-amber-200" />;
      case 'Trailblazer': return <TrendingUp size={size} className="text-orange-200" />;
      case 'Final Flourish': return <Zap size={size} className="text-blue-200" />;
      case 'Hot Streak': return <Activity size={size} className="text-red-200" />;
      case 'Bench Blunder': return <Package size={size} className="text-violet-200" />;
      case 'Tactical Genius': return <Target size={size} className="text-green-200" />;
      case 'Legendary Performance': return <Star size={size} className="text-purple-200" />;
      case 'Personal Best': return <Trophy size={size} className="text-blue-200" />;
      case 'FPL Manager': return <Award size={size} className="text-gray-200" />;
      default: return <Award size={size} className="text-gray-200" />;
    }
  };
  
  // Get badge description
  const getBadgeDescription = (badge) => {
    const descriptions = {
      'Diamond in the Rough': 'You scored big points with a differential player that others overlooked. Your eye for hidden talent paid off!',
      'On the Money': 'Your captain choice was among the top performing captains in the league that gameweek. Your leadership instincts were spot on!',
      'Full House': 'You achieved one of the top overall scores in the league for the gameweek. A masterclass in team selection!',
      'The Comeback': 'After a period in the doldrums, you staged an impressive recovery with a significant points haul.',
      'Mastermind': 'Your captain choice significantly outperformed the league average, showing your superior strategic thinking.',
      'Overachiever': 'You demolished the league average score this gameweek, performing well above expectations.',
      'Gameweek Champion': 'You achieved the highest overall score in the league for this gameweek. The ultimate FPL performance!',
      'Centurion': 'Breaking the magical 100 point barrier is a rare achievement that places you among FPL royalty. A truly spectacular gameweek!',
      'Trailblazer': 'You started the season with incredible momentum, ranking among the top performers in the opening month.',
      'Final Flourish': 'When it mattered most, you finished with a bang, significantly improving your rank in the final month of the season.',
      'Hot Streak': 'You maintained an impressive run of form, scoring above the league average for multiple consecutive gameweeks.',
      'Bench Blunder': 'Your bench scored an exceptional number of points this gameweek. A painful lesson in selection strategy!',
      'Tactical Genius': 'Despite having a below-average team value, you outperformed the league average, showing true managerial acumen.',
      'Legendary Performance': 'An exceptional gameweek score that puts you in the upper echelons of FPL performance.',
      'Personal Best': 'You achieved your highest score of the season in this gameweek. A moment of personal triumph!',
      'FPL Manager': 'You participated in this FPL season, embarking on the journey of fantasy football management.'
    };
    
    return descriptions[badge.name] || 'A special achievement in your FPL journey.';
  };
  
  // Get badge color based on name
  const getBadgeColor = (badgeName) => {
    const colors = {
      'Diamond in the Rough': 'from-cyan-600 to-blue-900',
      'On the Money': 'from-amber-600 to-amber-900',
      'Full House': 'from-purple-600 to-purple-900',
      'The Comeback': 'from-emerald-600 to-emerald-900',
      'Mastermind': 'from-indigo-600 to-indigo-900',
      'Overachiever': 'from-blue-600 to-blue-900',
      'Gameweek Champion': 'from-red-600 to-red-900',
      'Centurion': 'from-amber-500 to-red-800',
      'Trailblazer': 'from-orange-600 to-orange-900',
      'Final Flourish': 'from-blue-600 to-indigo-900',
      'Hot Streak': 'from-red-500 to-amber-700',
      'Bench Blunder': 'from-violet-600 to-violet-900',
      'Tactical Genius': 'from-green-600 to-green-900',
      'Legendary Performance': 'from-purple-500 to-indigo-900',
      'Personal Best': 'from-blue-600 to-blue-900',
      'FPL Manager': 'from-gray-600 to-gray-900'
    };
    
    return colors[badgeName] || 'from-gray-600 to-gray-900';
  };
  
  // Get category icon
  const getCategoryIcon = (category, size = 20) => {
    switch (category) {
      case 'Performance': return <Trophy size={size} className="text-purple-400" />;
      case 'Captain': return <Trophy size={size} className="text-amber-400" />;
      case 'Differential': return <Star size={size} className="text-cyan-400" />;
      case 'Consistency': return <Activity size={size} className="text-blue-400" />;
      case 'Milestones': return <Shield size={size} className="text-red-400" />;
      case 'Special': return <Zap size={size} className="text-green-400" />;
      default: return <Award size={size} className="text-gray-400" />;
    }
  };

  // Badge description display component - integrated into the main UI
  const BadgeInfoDisplay = ({ badge, isVisible, onClose }) => {
    if (!isVisible) return null;
    
    return (
      <div 
        className="mt-2 mb-4 rounded-lg p-4 animate-fade-in relative overflow-hidden" style={{ background: 'rgba(10,15,30,0.85)', border: '1px solid rgba(63,105,174,0.4)' }}
      >
        {/* Decorative accent */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#3f69ae] via-[#44d5a3] to-[#3f69ae]"></div>

        <div className="flex justify-between">
          <h4 className="text-[#44d5a3] font-medium mb-2">About this badge:</h4>
          <button
            onClick={onClose}
            className="text-[#44b6c5] hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        
        <div className="text-white/90 text-sm">
          {formatDescription(getBadgeDescription(badge))}
        </div>
      </div>
    );
  };
  
  // Gameweek detail modal - Using portal to center in viewport
  const GameweekDetailModal = ({ badge, gameweek, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);
    
    // Find the specific instance for this gameweek
    const instance = badge.instances.find(b => b.gameweek === gameweek) || badge.instances[0];
    
    // Effect for animation
    useEffect(() => {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
      
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);
      
      return () => {
        // Re-enable scrolling when modal is closed
        document.body.style.overflow = '';
        clearTimeout(timer);
      };
    }, []);
    
    // Create portal to render modal directly in document body
    return createPortal(
      <div 
        className="fixed inset-0 flex items-center justify-center" 
        style={{ 
          zIndex: 9999,
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onClose}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/70 backdrop-blur-md"
          style={{ position: 'absolute', inset: 0 }}
        ></div>
        
        {/* Modal content */}
        <div 
          className={`relative bg-gradient-to-br ${getBadgeColor(badge.name)} rounded-xl w-[90%] max-w-md shadow-2xl p-6 border-2 border-white/20 transition-all duration-300 ease-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          style={{ zIndex: 10000, maxWidth: '450px' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors rounded-full h-8 w-8 flex items-center justify-center bg-black/20 hover:bg-black/40"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-black/30 rounded-full flex items-center justify-center mr-3">
              {renderBadgeIcon(badge.name, 32)}
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{badge.name}</h3>
              <div className="text-white/70">Gameweek {gameweek} Achievement</div>
            </div>
          </div>
          
          <div className="bg-black/20 p-4 rounded-lg text-white/90 mt-2">
            {formatDescription(getBadgeDescription(badge))}
          </div>
          
          {instance.details && (
            <div className="mt-3 text-white/80 bg-black/20 p-4 rounded-lg">
              <h4 className="text-white font-medium mb-1">Details:</h4>
              <p>{instance.details}</p>
            </div>
          )}
          
          <button 
            onClick={onClose}
            className="mt-5 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full mx-auto block transition-colors"
          >
            Close
          </button>
        </div>
      </div>,
      document.body // This ensures the modal is rendered directly in the body
    );
  };
  
  const [isBadgeSectionCollapsed, setIsBadgeSectionCollapsed] = useState(false);

  return (
    <div className="bg-black/30 backdrop-blur-sm rounded-xl p-5 border-2 border-[#3f69ae]/30">
      {/* Header with collapse button */}
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-xl font-bold text-white flex items-center">
          <Award size={20} className="mr-2 text-amber-400" />
          Player Badges ({badges.length})
        </h3>
        
        <button 
          onClick={() => setIsBadgeSectionCollapsed(!isBadgeSectionCollapsed)}
          className="px-3 py-1.5 bg-[#3f69ae]/20 hover:bg-[#3f69ae]/35 text-[#44d5a3] rounded-full text-sm flex items-center transition-colors"
        >
          {isBadgeSectionCollapsed ? (
            <>
              <ChevronRight size={16} className="mr-1" />
              Show Badges
            </>
          ) : (
            <>
              <ChevronDown size={16} className="mr-1" />
              Hide Badges
            </>
          )}
        </button>
      </div>
      
      {/* Badge content - only show if not collapsed */}
      {!isBadgeSectionCollapsed && (
        <div className="space-y-4">
          {Object.keys(badgesByCategory).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(badgesByCategory).map(([category, categoryBadges]) => (
                <div key={category} className="bg-[#3f69ae]/10 rounded-lg overflow-hidden border border-[#3f69ae]/30">
                  {/* Category header (clickable to expand/collapse) */}
                  <div 
                    className="flex items-center justify-between px-4 py-3 bg-[#3f69ae]/15 cursor-pointer hover:bg-[#3f69ae]/25 transition-colors"
                    onClick={() => toggleCategory(category)}
                  >
                    <div className="flex items-center space-x-2">
                      {getCategoryIcon(category)}
                      <h4 className="font-medium text-white">{category} Badges</h4>
                      <span className="text-sm text-[#44d5a3]">({categoryBadges.length})</span>
                    </div>
                    <div>
                      {expandedCategories[category] ? 
                        <ChevronDown size={20} className="text-indigo-300" /> : 
                        <ChevronRight size={20} className="text-indigo-300" />}
                    </div>
                  </div>
                  
                  {/* Badge rows (shown when category is expanded) */}
                  {expandedCategories[category] && (
                    <div className="divide-y divide-[#3f69ae]/20">
                      {categoryBadges.map((badge, index) => (
                        <div 
                          key={index}
                          className="px-4 py-3 hover:bg-[#3f69ae]/10 transition-colors"
                        >
                          <div className="flex items-center mb-2 justify-between">
                            <div className="flex items-center">
                              {/* Small badge icon */}
                              <div className="w-8 h-8 bg-[#3f69ae]/20 rounded-full flex items-center justify-center mr-3">
                                {renderBadgeIcon(badge.name)}
                              </div>
                              
                              <div className="flex items-center">
                                {/* Badge name */}
                                <div className="font-medium text-white">{badge.name}</div>
                                
                                {/* Info button - now toggles the info display */}
                                <button 
                                  onClick={() => setVisibleInfoBadge(visibleInfoBadge === badge.name ? null : badge.name)}
                                  className={`p-1.5 rounded-full ${visibleInfoBadge === badge.name ? 'bg-[#3f69ae] text-white' : 'bg-[#3f69ae]/20 text-[#44d5a3] hover:bg-[#3f69ae]/35 hover:text-white'} transition-colors ml-3`}
                                  aria-label="Toggle badge information"
                                >
                                  <AlertCircle size={16} />
                                </button>
                              </div>
                            </div>
                            
                            {/* Badge count */}
                            <div className="text-xs text-indigo-300 bg-[#3f69ae]/15 px-2 py-1 rounded-full">
                              {badge.gameweeks.length} {badge.gameweeks.length === 1 ? 'time' : 'times'}
                            </div>
                          </div>
                          
                          {/* Badge info section - appears when info icon is clicked */}
                          <BadgeInfoDisplay 
                            badge={badge}
                            isVisible={visibleInfoBadge === badge.name}
                            onClose={() => setVisibleInfoBadge(null)}
                          />
                          
                          {/* Gameweeks row displayed as trophies */}
                          <div className="flex flex-wrap gap-2 mt-1 ml-11">
                            {badge.gameweeks.map((gw, gwIndex) => {
                              // Determine badge style based on gameweek number
                              const baseColor = `bg-gradient-to-br from-[#1a2e5a] to-[#0d1a34]`;
                              const specialColor = gw % 10 === 0 ? 'from-amber-600 to-amber-800' : // Milestone GWs (10, 20, etc)
                                             gw % 5 === 0 ? 'from-purple-600 to-purple-800' : // Semi-milestone GWs (5, 15, etc)
                                             gw > 30 ? 'from-emerald-600 to-emerald-800' : // Late season GWs
                                             gw < 10 ? 'from-blue-600 to-blue-800' : // Early season GWs
                                             baseColor;
                              
                              return (
                                <div 
                                  key={gwIndex}
                                  onClick={() => setSelectedBadge({ ...badge, selectedGameweek: gw })}
                                  className={`w-10 h-10 bg-gradient-to-br ${specialColor} rounded-full flex items-center justify-center text-xs font-medium text-white hover:brightness-110 cursor-pointer transition-all transform hover:scale-110 border border-[#3f69ae]/30 shadow-md relative overflow-hidden`}
                                  title={`Earned in Gameweek ${gw}`}
                                >
                                  {/* Shiny reflection effect */}
                                  <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/10 to-transparent opacity-50"></div>
                                  
                                  {/* Small star accent for special gameweeks */}
                                  {(gw % 5 === 0 || gw > 30) && (
                                    <div className="absolute top-0.5 right-0.5 w-1.5 h-1.5 bg-yellow-300 rounded-full animate-pulse-slow"></div>
                                  )}
                                  
                                  {gw}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-400 py-6">
              No badges earned yet.
            </div>
          )}
        </div>
      )}
      
      {/* Gameweek detail modal */}
      {selectedBadge && (
        <GameweekDetailModal 
          badge={selectedBadge} 
          gameweek={selectedBadge.selectedGameweek}
          onClose={() => setSelectedBadge(null)} 
        />
      )}
      
      {/* Add these styles to your CSS */}
      <style jsx>{`
        @keyframes shine {
          0% { transform: translateX(-100%) rotate(-45deg); }
          100% { transform: translateX(100%) rotate(-45deg); }
        }
        
        .animate-shine {
          animation: shine 2s infinite linear;
        }
        
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
        
        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        
        .shine-animation {
          transition: opacity 0.3s ease;
        }
        
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default PlayerBadges;
